import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tag, Download, User, RefreshCw, ExternalLink, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import LisjakSyncReportDialog from "../components/LisjakSyncReportDialog";

const MESECI = ["", "jan", "feb", "mar", "apr", "maj", "jun", "jul", "avg", "sep", "okt", "nov", "dec"];
const fmtMonth = (m) => {
  const [y, mo] = String(m).split("-");
  return `${MESECI[parseInt(mo, 10)] || mo} ${y}`;
};

const STATUS_COLORS = {
  prosto: "bg-green-100 text-green-800 border-green-300",
  dodeljena: "bg-green-100 text-green-800 border-green-300",
  uporabljena: "bg-red-100 text-red-800 border-red-300",
  vrnjena: "bg-green-100 text-green-800 border-green-300",
};

const STATUS_LABEL = {
  prosto: "neuporabljena",
  dodeljena: "neuporabljena",
  uporabljena: "uporabljena",
  vrnjena: "neuporabljena",
};

const VezicePage = () => {
  const { token, hasRole } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canEdit = hasRole(["predsednik", "gospodar", "tajnik", "administrator"]);

  const [tags, setTags] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState(null); // dialog data
  const [stats, setStats] = useState(null);
  const [showStats, setShowStats] = useState(false);

  const syncLisjak = async () => {
    if (!window.confirm("Zaženem sinhronizacijo z Lisjakom? Nove vezice iz Lisjaka bodo dodane, spremembe za obstoječe pa gredo v čakajoče spremembe (Upravljanje → Lisjak).")) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/lisjak/sync-now`, {}, { headers });
      setSyncReport(res.data);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri sinhronizaciji. Preveri Lisjak konfiguracijo v Upravljanje → Lisjak.");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    try {
      const promises = [axios.get(`${API}/vezice/tags`, { headers })];
      if (canEdit) {
        promises.push(axios.get(`${API}/users`, { headers }));
      }
      const [t, m] = await Promise.all(promises);
      setTags(t.data || []);
      setMembers(m ? (m.data || []) : []);
      if (canEdit) {
        axios.get(`${API}/vezice/stats`, { headers }).then((r) => setStats(r.data)).catch(() => {});
      }
    } catch (e) {
      toast.error("Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  const memberName = (id) => {
    const m = members.find(x => x.id === id);
    return m ? `${m.ime} ${m.priimek}` : "";
  };

  const filtered = tags.filter(t => {
    if (filterYear && String(t.leto || "") !== filterYear) return false;
    if (filterStatus) {
      const isUsed = t.status === "uporabljena";
      if (filterStatus === "uporabljena" && !isUsed) return false;
      if (filterStatus === "neuporabljena" && isUsed) return false;
    }
    if (q) {
      const name = memberName(t.dodeljen_id).toLowerCase();
      const s = q.toLowerCase();
      return t.stevilka.toLowerCase().includes(s) || name.includes(s);
    }
    return true;
  });

  const years = Array.from(new Set(tags.map(t => t.leto).filter(Boolean))).sort((a, b) => b - a);

  const exportCSV = () => {
    const rows = [["stevilka", "leto", "izkaznica", "clan", "status"]];
    filtered.forEach(t => {
      const m = members.find(x => x.id === t.dodeljen_id);
      rows.push([
        t.stevilka,
        t.leto || "",
        m?.lovska_izkaznica || "",
        m ? `${m.ime} ${m.priimek}` : "",
        STATUS_LABEL[t.status] || t.status || "",
      ]);
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "vezice.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vezice-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Tag className="h-8 w-8" />
            Vezice
          </h1>
          <p className="text-muted-foreground mt-1">Evidenca vezic (samodejna sinhronizacija iz Lisjaka)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => window.open("https://lisjak.lovska-zveza.si/Odvzem/Vezica/glavno.aspx", "_blank", "noopener,noreferrer")}
            data-testid="open-lisjak-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />Odpri v Lisjaku
          </Button>
          {canEdit && (
            <>
              <Button
                variant={showStats ? "default" : "outline"}
                onClick={() => setShowStats((v) => !v)}
                data-testid="vezice-stats-btn"
              >
                <BarChart3 className="h-4 w-4 mr-2" />Statistika porabe
              </Button>
              <Button
                variant="outline"
                onClick={syncLisjak}
                disabled={syncing}
                data-testid="sync-lisjak-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Sinhroniziram ..." : "Sinhroniziraj z Lisjakom"}
              </Button>
              <Button variant="outline" onClick={exportCSV} data-testid="export-vezice-btn">
                <Download className="h-4 w-4 mr-2" />Izvozi CSV
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Statistika porabe (vodstvo) */}
      {canEdit && showStats && stats && (
        <Card data-testid="vezice-stats-card">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-2 border">
                <div className="text-xs text-slate-500">Skupaj vezic</div>
                <div className="text-lg font-bold text-slate-800" data-testid="stat-total">{stats.total}</div>
              </div>
              <div className="bg-red-50 rounded-lg p-2 border border-red-100">
                <div className="text-xs text-slate-500">Uporabljene</div>
                <div className="text-lg font-bold text-red-700" data-testid="stat-used">{stats.uporabljene}</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-100">
                <div className="text-xs text-slate-500">Dodeljene (neuporabljene)</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="stat-assigned">{stats.dodeljene}</div>
              </div>
              <div className="bg-sky-50 rounded-lg p-2 border border-sky-100">
                <div className="text-xs text-slate-500">Proste</div>
                <div className="text-lg font-bold text-sky-700" data-testid="stat-free">{stats.proste}</div>
              </div>
            </div>

            {stats.by_month?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Poraba vezic po mesecih</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={stats.by_month.map((m) => ({ ...m, label: fmtMonth(m.mesec) }))} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <RTooltip formatter={(v) => [v, "Uporabljene vezice"]} contentStyle={{ fontSize: 11 }} />
                    <Bar dataKey="uporabljene" fill="#dc2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {stats.by_member?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-700 mb-1">Poraba po članih</p>
                <div className="max-h-72 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr className="text-left text-slate-500">
                        <th className="py-1.5 px-2">Član</th>
                        <th className="py-1.5 px-2 text-center">Uporabljene</th>
                        <th className="py-1.5 px-2 text-center">Dodeljene (še na voljo)</th>
                      </tr>
                    </thead>
                    <tbody data-testid="vezice-stats-members">
                      {stats.by_member.map((m) => (
                        <tr key={m.user_id} className="border-t">
                          <td className="py-1 px-2">{m.clan}</td>
                          <td className={`py-1 px-2 text-center font-semibold ${m.uporabljene > 0 ? "text-red-700" : "text-slate-400"}`}>{m.uporabljene}</td>
                          <td className="py-1 px-2 text-center text-emerald-700 font-semibold">{m.dodeljene}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Iskanje</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Številka, žival, član ..." data-testid="vezice-search" />
          </div>
          <div>
            <Label className="text-xs">Leto</Label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="border rounded-md h-10 px-3 text-sm">
              <option value="">Vsa</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded-md h-10 px-3 text-sm">
              <option value="">Vsi</option>
              <option value="neuporabljena">Neuporabljene (zelena)</option>
              <option value="uporabljena">Uporabljene (rdeča)</option>
            </select>
          </div>
          <div className="text-sm text-muted-foreground pb-2">
            Prikazano: <b>{filtered.length}</b> / {tags.length}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-left p-2">Številka</th>
                  <th className="text-left p-2">Leto</th>
                  <th className="text-left p-2">Zadolženi član</th>
                  <th className="text-left p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Ni zapisov</td></tr>
                )}
                {filtered.map(t => {
                  const isUsed = t.status === "uporabljena";
                  return (
                  <tr key={t.id} className="border-t hover:bg-muted/30" data-testid={`vezica-row-${t.id}`}>
                    <td className="p-2">
                      <span className={`inline-block px-2 py-1 rounded font-mono font-semibold border ${isUsed ? "bg-red-100 text-red-800 border-red-300" : "bg-green-100 text-green-800 border-green-300"}`}>
                        {t.stevilka}
                      </span>
                    </td>
                    <td className="p-2">{t.leto || "—"}</td>
                    <td className="p-2">
                      {t.dodeljen_id ? (
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {memberName(t.dodeljen_id)}
                        </span>
                      ) : <span className="text-muted-foreground italic">nedodeljena</span>}
                    </td>
                    <td className="p-2">
                      <Badge className={STATUS_COLORS[t.status] || ""} variant="secondary">{STATUS_LABEL[t.status] || t.status}</Badge>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <LisjakSyncReportDialog open={!!syncReport} data={syncReport} onClose={() => setSyncReport(null)} />
    </div>
  );
};

export default VezicePage;
